// frontend/src/pages/PartsPage.js
import React, { useState, useEffect } from 'react';
import { partsApi } from '../api/parts';
import { adminApi } from '../api/admin';
import PartCard from '../components/part/PartCard'; 
import { FaSearch, FaFilter, FaChevronDown, FaChevronRight, FaTimes, FaCheckSquare, FaSquare, FaSortAmountDown, FaSpinner } from 'react-icons/fa';
import helpers from '../utils/helpers'; // <--- FIXED IMPORT PATH (was ../../utils/helpers)

// --- DATA STRUCTURES ---
const CATEGORY_GROUPS = {
    "Engine Components": [
        { id: 'engine_components', label: 'All Engine Parts' },
        { id: 'air_filters', label: 'Air Filters' },
        { id: 'oil_filters', label: 'Oil Filters' },
        { id: 'spark_plugs', label: 'Spark Plugs' },
        { id: 'ignition_coils', label: 'Ignition Coils' },
        { id: 'belts_hoses', label: 'Belts & Hoses' },
        { id: 'gaskets_seals', label: 'Gaskets & Seals' },
        { id: 'pistons_rings', label: 'Pistons & Rings' }
    ],
    "Brake System": [
        { id: 'brake_system', label: 'All Brake Parts' },
        { id: 'brake_pads', label: 'Brake Pads' },
        { id: 'brake_rotors', label: 'Brake Rotors/Discs' },
        { id: 'brake_calipers', label: 'Brake Calipers' },
        { id: 'brake_fluid', label: 'Brake Fluid' },
        { id: 'brake_shoes', label: 'Brake Shoes' }
    ],
    "Suspension & Steering": [
        { id: 'suspension_steering', label: 'All Suspension' },
        { id: 'shock_absorbers', label: 'Shock Absorbers' },
        { id: 'control_arms', label: 'Control Arms' },
        { id: 'ball_joints', label: 'Ball Joints' },
        { id: 'tie_rod_ends', label: 'Tie Rod Ends' },
        { id: 'steering_racks', label: 'Steering Racks' }
    ],
    "Transmission": [
        { id: 'transmission_drivetrain', label: 'All Transmission' },
        { id: 'clutch_kits', label: 'Clutch Kits' },
        { id: 'cv_joints_axles', label: 'CV Joints & Axles' },
        { id: 'transmission_fluid', label: 'Transmission Fluid' }
    ],
    "Electrical": [
        { id: 'electrical_system', label: 'All Electrical' },
        { id: 'batteries', label: 'Batteries' },
        { id: 'alternators', label: 'Alternators' },
        { id: 'starters', label: 'Starters' },
        { id: 'sensors', label: 'Sensors' },
        { id: 'fuses_relays', label: 'Fuses & Relays' }
    ],
    "Body & Exterior": [
        { id: 'body_exterior', label: 'All Body Parts' },
        { id: 'headlights', label: 'Headlights' },
        { id: 'tail_lights', label: 'Tail Lights' },
        { id: 'bumpers', label: 'Bumpers' },
        { id: 'mirrors', label: 'Mirrors' },
        { id: 'wipers', label: 'Wiper Blades' }
    ],
    "Tires & Wheels": [
        { id: 'tires_wheels', label: 'All Tires/Wheels' },
        { id: 'tires', label: 'Tires' },
        { id: 'wheels_rims', label: 'Wheels & Rims' },
        { id: 'wheel_bearings', label: 'Wheel Bearings' }
    ],
    "Fluids & Chemicals": [
        { id: 'fluids_chemicals', label: 'All Fluids' },
        { id: 'engine_oil', label: 'Engine Oil' },
        { id: 'coolant', label: 'Coolant' }
    ],
    "Other Systems": [
        { id: 'cooling_heating', label: 'Cooling & Heating' },
        { id: 'exhaust_system', label: 'Exhaust System' },
        { id: 'fuel_system', label: 'Fuel System' },
        { id: 'interior_components', label: 'Interior' },
        { id: 'tools_equipment', label: 'Tools' }
    ]
};

const PART_BRANDS = [
    { id: 'toyota_genuine', label: 'Toyota' },
    { id: 'honda_oem', label: 'Honda' },
    { id: 'ford_motorcraft', label: 'Ford' },
    { id: 'mercedes_benz_genuine', label: 'Mercedes' },
    { id: 'bmw_oem', label: 'BMW' },
    { id: 'denso', label: 'Denso' },
    { id: 'bosch', label: 'Bosch' },
    { id: 'ngk', label: 'NGK' },
    { id: 'valeo', label: 'Valeo' },
    { id: 'kyb', label: 'KYB' },
    { id: 'mobil_1', label: 'Mobil 1' },
    { id: 'total', label: 'Total' },
    { id: 'michelin', label: 'Michelin' },
    { id: 'rana_motors', label: 'Rana Motors' },
    { id: 'union_autoparts', label: 'Union Autoparts' },
    { id: 'kaydee', label: 'Kaydee' },
    { id: 'lucas_tvs', label: 'Lucas TVS' },
    { id: 'proforce', label: 'Proforce' },
    { id: 'bat', label: 'BAT' },
    { id: 'zikoko', label: 'Zikoko' },
];

const CONDITIONS = [
    'New (In Box)', 
    'New (Open Box)', 
    'Remanufactured', 
    'Used (Tested)', 
    'Used (Untested)'
];

// Price Range Config
const MIN_PRICE_LIMIT = 0;
// UPDATED: Increased to 100 Million to accommodate vehicles and expensive parts
const MAX_PRICE_LIMIT = 100000000; 

function PartsPage() {
  const [parts, setParts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Filter States
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedBrands, setSelectedBrands] = useState([]); 
  const [selectedCondition, setSelectedCondition] = useState('');
  const [isOemOnly, setIsOemOnly] = useState(false);
  
  // Price State
  const [priceRange, setPriceRange] = useState({ min: null, max: null });
  const [localPrice, setLocalPrice] = useState({ min: MIN_PRICE_LIMIT, max: MAX_PRICE_LIMIT });
  
  const [sortBy, setSortBy] = useState('newest');
  
  // UI States
  const [expandedCategory, setExpandedCategory] = useState(null);
  const [showMobileFilters, setShowMobileFilters] = useState(false);

  const fetchParts = async (isSearch = false) => {
    setLoading(true);
    setError(null);
    try {
      const filters = {
        q: searchTerm,
        make: selectedBrands.join(','), 
        category: selectedCategory,
        condition: selectedCondition,
        is_oem: isOemOnly ? 'true' : null,
        min_price: priceRange.min,
        max_price: priceRange.max,
        sort_by: sortBy,
        limit: 50
      };
      
      console.log(">>> [PartsPage] Fetching with filters:", filters);

      const data = await partsApi.getAllParts(filters);
      setParts(data);

      if (isSearch && searchTerm.trim()) {
          adminApi.logSearch(searchTerm, data.length);
      }

    } catch (err) {
      console.error("Error fetching parts:", err);
      setError("Failed to load parts. If filtering by price, check backend terminal for index link.");
    } finally {
      setLoading(false);
    }
  };

  // Trigger fetch when Applied Filters change
  useEffect(() => {
    fetchParts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCategory, selectedBrands, selectedCondition, isOemOnly, priceRange, sortBy]); 

  const handleSearch = (e) => {
    e.preventDefault();
    fetchParts(true);
  };

  // --- PRICE HANDLERS ---
  const handleSliderChange = (e) => {
      const { name, value } = e.target;
      const val = parseInt(value);
      
      if (name === 'min') {
          if (val < localPrice.max) setLocalPrice(prev => ({ ...prev, min: val }));
      } else {
          if (val > localPrice.min) setLocalPrice(prev => ({ ...prev, max: val }));
      }
  };

  const handlePriceInputChange = (e) => {
      const { name, value } = e.target;
      setLocalPrice(prev => ({ ...prev, [name]: value }));
  };

  const handleApplyPrice = () => {
      console.log(">>> [PartsPage] Applying Price Range:", localPrice);
      setPriceRange({ 
          min: localPrice.min > MIN_PRICE_LIMIT ? localPrice.min : null, 
          max: localPrice.max < MAX_PRICE_LIMIT ? localPrice.max : null 
      });
  };

  const toggleCategory = (groupName) => {
    setExpandedCategory(expandedCategory === groupName ? null : groupName);
  };

  const toggleBrand = (brandId) => {
      setSelectedBrands(prev => 
          prev.includes(brandId) 
          ? prev.filter(b => b !== brandId) 
          : [...prev, brandId]
      );
  };

  const clearFilters = () => {
      setSearchTerm('');
      setSelectedCategory('');
      setSelectedBrands([]);
      setSelectedCondition('');
      setIsOemOnly(false);
      setLocalPrice({ min: MIN_PRICE_LIMIT, max: MAX_PRICE_LIMIT });
      setPriceRange({ min: null, max: null });
      setSortBy('newest');
      
      // Force immediate fetch
      setLoading(true);
      partsApi.getAllParts({}).then(data => {
        setParts(data);
        setLoading(false);
      }).catch(() => setLoading(false));
  };

  // Calculate percentage for slider track background
  const minPercent = ((localPrice.min - MIN_PRICE_LIMIT) / (MAX_PRICE_LIMIT - MIN_PRICE_LIMIT)) * 100;
  const maxPercent = ((localPrice.max - MIN_PRICE_LIMIT) / (MAX_PRICE_LIMIT - MIN_PRICE_LIMIT)) * 100;

  return (
    <div className="bg-gray-50 min-h-screen py-8">
      <div className="container mx-auto px-4">
        
        {/* --- HEADER & MOBILE FILTER TOGGLE --- */}
        <div className="flex flex-col md:flex-row justify-between items-center mb-6 gap-4">
            <h1 className="text-3xl font-bold text-gray-800">Parts Inventory</h1>
            
            <div className="flex w-full md:w-auto gap-2">
                <form onSubmit={handleSearch} className="relative flex-grow md:w-80">
                    <input 
                        type="text" 
                        placeholder="Search parts..." 
                        className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:outline-none"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                    <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                </form>
                <button 
                    className="md:hidden bg-white border border-gray-300 px-4 py-2 rounded-lg text-gray-700 flex items-center gap-2"
                    onClick={() => setShowMobileFilters(!showMobileFilters)}
                >
                    <FaFilter /> Filters
                </button>
            </div>
        </div>

        <div className="flex flex-col md:flex-row gap-8">
            
            {/* --- SIDEBAR FILTERS --- */}
            <aside className={`md:w-1/4 bg-white p-5 rounded-lg shadow-sm h-fit ${showMobileFilters ? 'block' : 'hidden md:block'}`}>
                <div className="flex justify-between items-center mb-4">
                    <h3 className="font-bold text-gray-800 text-lg">Filters</h3>
                    {(selectedCategory || selectedBrands.length > 0 || selectedCondition || isOemOnly || priceRange.min || priceRange.max) && (
                        <button onClick={clearFilters} className="text-xs text-red-500 hover:underline">Clear All</button>
                    )}
                </div>

                {/* SORTING */}
                <div className="mb-6">
                    <h4 className="font-semibold text-gray-700 mb-2 uppercase text-xs tracking-wider flex items-center gap-2">
                        <FaSortAmountDown /> Sort By
                    </h4>
                    <select 
                        value={sortBy} 
                        onChange={(e) => setSortBy(e.target.value)}
                        className="w-full p-2 border rounded text-sm bg-gray-50"
                    >
                        <option value="newest">Newest Arrivals</option>
                        <option value="price_asc">Price: Low to High</option>
                        <option value="price_desc">Price: High to Low</option>
                    </select>
                </div>

                {/* PRICE RANGE (SLIDER + INPUTS + BUTTON) */}
                <div className="mb-6">
                    <h4 className="font-semibold text-gray-700 mb-2 uppercase text-xs tracking-wider">Price Range (NGN)</h4>
                    
                    {/* Slider */}
                    <div className="range-slider mb-4">
                        <div 
                            className="track" 
                            style={{ left: `${minPercent}%`, width: `${maxPercent - minPercent}%` }}
                        ></div>
                        <input 
                            type="range" 
                            name="min" 
                            min={MIN_PRICE_LIMIT} 
                            max={MAX_PRICE_LIMIT} 
                            value={localPrice.min} 
                            onChange={handleSliderChange} 
                        />
                        <input 
                            type="range" 
                            name="max" 
                            min={MIN_PRICE_LIMIT} 
                            max={MAX_PRICE_LIMIT} 
                            value={localPrice.max} 
                            onChange={handleSliderChange} 
                        />
                    </div>

                    {/* Manual Inputs */}
                    <div className="flex gap-2 mb-3">
                        <input 
                            type="number" 
                            name="min"
                            placeholder="Min" 
                            value={localPrice.min}
                            onChange={handlePriceInputChange}
                            className="w-1/2 p-2 border rounded text-sm"
                        />
                        <input 
                            type="number" 
                            name="max"
                            placeholder="Max" 
                            value={localPrice.max}
                            onChange={handlePriceInputChange}
                            className="w-1/2 p-2 border rounded text-sm"
                        />
                    </div>

                    {/* APPLY BUTTON */}
                    <button 
                        onClick={handleApplyPrice}
                        className="w-full bg-primary text-white text-xs font-bold py-2 rounded hover:bg-primary-dark transition-colors shadow-sm flex justify-center items-center gap-2"
                    >
                        {loading ? <FaSpinner className="animate-spin" /> : 'Apply Price Filter'}
                    </button>
                </div>

                {/* OEM TOGGLE */}
                <div className="mb-6 pb-4 border-b border-gray-100">
                    <label className="flex items-center cursor-pointer gap-2">
                        <div onClick={() => setIsOemOnly(!isOemOnly)} className="text-primary text-lg">
                            {isOemOnly ? <FaCheckSquare /> : <FaSquare className="text-gray-300" />}
                        </div>
                        <span className="text-sm font-bold text-gray-700">Genuine OEM Only</span>
                    </label>
                </div>

                {/* CONDITION */}
                <div className="mb-6">
                    <h4 className="font-semibold text-gray-700 mb-3 uppercase text-xs tracking-wider">Condition</h4>
                    <select 
                        value={selectedCondition} 
                        onChange={(e) => setSelectedCondition(e.target.value)}
                        className="w-full p-2 border rounded text-sm bg-gray-50"
                    >
                        <option value="">Any Condition</option>
                        {CONDITIONS.map(c => <option key={c} value={c}>{c}</option>)}
                    </select>
                </div>

                {/* CATEGORIES */}
                <div className="mb-6">
                    <h4 className="font-semibold text-gray-700 mb-3 uppercase text-xs tracking-wider">Categories</h4>
                    <div className="space-y-1">
                        {Object.keys(CATEGORY_GROUPS).map((group) => (
                            <div key={group} className="border-b border-gray-100 last:border-0">
                                <button 
                                    onClick={() => toggleCategory(group)}
                                    className="w-full flex justify-between items-center py-2 text-sm text-gray-600 hover:text-primary transition-colors"
                                >
                                    <span>{group}</span>
                                    {expandedCategory === group ? <FaChevronDown size={10} /> : <FaChevronRight size={10} />}
                                </button>
                                
                                {/* Sub-Categories */}
                                {expandedCategory === group && (
                                    <div className="pl-4 pb-2 space-y-1 bg-gray-50 rounded-b-lg">
                                        {CATEGORY_GROUPS[group].map((sub) => (
                                            <button
                                                key={sub.id}
                                                onClick={() => setSelectedCategory(sub.id)}
                                                className={`block w-full text-left text-xs py-1.5 px-2 rounded transition-colors ${
                                                    selectedCategory === sub.id 
                                                    ? 'bg-blue-100 text-blue-700 font-bold' 
                                                    : 'text-gray-500 hover:bg-gray-200'
                                                }`}
                                            >
                                                {sub.label}
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                </div>

                {/* BRANDS */}
                <div>
                    <h4 className="font-semibold text-gray-700 mb-3 uppercase text-xs tracking-wider">Brands</h4>
                    <div className="space-y-1 max-h-60 overflow-y-auto pr-2 custom-scrollbar">
                        {PART_BRANDS.map((brand) => (
                            <label key={brand.id} className="flex items-center space-x-2 cursor-pointer py-1 hover:bg-gray-50 rounded px-1">
                                <input 
                                    type="checkbox" 
                                    checked={selectedBrands.includes(brand.id)}
                                    onChange={() => toggleBrand(brand.id)}
                                    className="form-checkbox text-primary h-4 w-4 rounded border-gray-300 focus:ring-primary"
                                />
                                <span className={`text-sm ${selectedBrands.includes(brand.id) ? 'text-primary font-bold' : 'text-gray-600'}`}>
                                    {brand.label}
                                </span>
                            </label>
                        ))}
                    </div>
                </div>
            </aside>

            {/* --- MAIN CONTENT GRID --- */}
            <main className="md:w-3/4">
                {/* Active Filters Display */}
                {(selectedCategory || selectedBrands.length > 0 || selectedCondition || isOemOnly || priceRange.min || priceRange.max) && (
                    <div className="mb-4 flex flex-wrap gap-2">
                        {isOemOnly && (
                            <span className="bg-blue-600 text-white text-xs px-3 py-1 rounded-full flex items-center gap-1">
                                Genuine OEM <button onClick={() => setIsOemOnly(false)}><FaTimes /></button>
                            </span>
                        )}
                        {(priceRange.min || priceRange.max) && (
                            <span className="bg-green-100 text-green-800 text-xs px-3 py-1 rounded-full flex items-center gap-1">
                                ₦{helpers.formatCurrency(priceRange.min || 0)} - ₦{helpers.formatCurrency(priceRange.max || MAX_PRICE_LIMIT)} 
                                <button onClick={() => { setLocalPrice({min: MIN_PRICE_LIMIT, max: MAX_PRICE_LIMIT}); setPriceRange({min: null, max: null}); }}><FaTimes /></button>
                            </span>
                        )}
                        {selectedCondition && (
                            <span className="bg-gray-200 text-gray-800 text-xs px-3 py-1 rounded-full flex items-center gap-1">
                                {selectedCondition} <button onClick={() => setSelectedCondition('')}><FaTimes /></button>
                            </span>
                        )}
                        {selectedCategory && (
                            <span className="bg-blue-100 text-blue-800 text-xs px-3 py-1 rounded-full flex items-center gap-1">
                                Cat: {selectedCategory.replace(/_/g, ' ')} 
                                <button onClick={() => setSelectedCategory('')}><FaTimes /></button>
                            </span>
                        )}
                        {selectedBrands.map(brandId => (
                            <span key={brandId} className="bg-blue-100 text-blue-800 text-xs px-3 py-1 rounded-full flex items-center gap-1">
                                {PART_BRANDS.find(b => b.id === brandId)?.label || brandId}
                                <button onClick={() => toggleBrand(brandId)}><FaTimes /></button>
                            </span>
                        ))}
                    </div>
                )}

                {loading ? (
                    <div className="flex justify-center items-center h-64">
                        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
                    </div>
                ) : error ? (
                    <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative">
                        {error}
                    </div>
                ) : parts.length === 0 ? (
                    <div className="text-center py-16 bg-white rounded-lg shadow-sm">
                        <h3 className="text-xl text-gray-600">No parts found.</h3>
                        <p className="text-gray-500 mt-2">Try adjusting your filters or search term.</p>
                        <button onClick={clearFilters} className="mt-4 text-primary hover:underline">Clear All Filters</button>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                        {parts.map((part) => (
                            <PartCard key={part.id} part={part} />
                        ))}
                    </div>
                )}
            </main>

        </div>
      </div>
    </div>
  );
}

export default PartsPage;